import React, { useEffect, useMemo, useState } from 'react';
import { EventDate, SetLoading } from '@arema/components/Classes';
import { fromUnix, getNow, toggleArray } from '@arema/components/Util';
import { DateTime } from 'luxon';
import { Button, Header, Icon, Input, Message, Modal, Pagination, SegmentControl, Table } from 'react-frontier2';
import { Trans, useTranslation } from 'react-i18next';
import { firstBy } from 'thenby';
import API from '../API';

interface SelectDateModalProps{
	open: boolean,
	onClose: ()=>void,
	eventId: number,
	allEvent?: boolean,
	title?: string,
	pageSize?: number,
	multiple?: boolean,
	onSelected: (dates: EventDate[], setLoading: SetLoading)=>void,
}

enum FilterType{
	DATES = 1,
	RANGE = 2,
	ALL_EVENT = 99,
}

var SelectDateModal = (props: SelectDateModalProps)=>{
	var { t } = useTranslation();
	var [data, setData] = useState<{
		event_id: number,
		dates: EventDate[]
	}>(null);
	var [page, setPage] = useState<number>(0);
	var [selectedDates, setSelectedDates] = useState<number[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [filterType, setFilterType] = useState<FilterType>(FilterType.DATES);
	var [filterRange, setFilterRange] = useState<{
		start: DateTime,
		end: DateTime,
	}>(null);

	const PAGE_SIZE = props.pageSize || 20;

	useEffect(()=>{
		if(!props.open || !props.eventId) return;
		if(data?.event_id===props.eventId) return;

		setPage(0);
		setData(null);
		API.getEventDates(props.eventId).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData({
				event_id: props.eventId,
				dates: res.data
			});
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-SLCDTMD-1' }));
		});
	}, [props.eventId, props.open]);

	useEffect(()=>{
		setSelectedDates(null);
		setPage(0);
		setFilterType(FilterType.DATES);
		setFilterRange({
			start: getNow().minus({ days: 7 }).startOf('day'),
			end: getNow().endOf('day'),
		});
	}, [props.open]);

	var onDateSelected = (date_id: number)=>{
		return ()=>{
			if(props.multiple===false){
				var dt = data.dates.find(a=>a.date_id===date_id);
				return props.onSelected([dt], ()=>{});
			}else{
				setSelectedDates(toggleArray(selectedDates, date_id));
			}
		}
	}

	var now = useMemo(()=>{
		return getNow();
	}, [props.open]);

	var filter_prediction = useMemo(()=>{
		if(!data?.dates || !filterRange || !filterRange.start || !filterRange.end) return 0;

		var ds = filterRange.start, de = filterRange.end;
		if(ds.toSeconds()>de.toSeconds()){
			var t = ds;
			ds = de;
			de = t;
		}

		ds = ds.startOf('day');
		de = de.endOf('day');

		return data.dates.filter(a=>a.date>=ds.toSeconds() && a.date<=de.toSeconds()).length;
	}, [data, filterRange]);

	var DatesList = useMemo(()=>{
		if(!data) return [];
		var now = getNow().toSeconds();
		var sorted_dates = data.dates.sort(firstBy((a: EventDate, b: EventDate)=>{
			return (a.date>=now ? 0 : 1) - (b.date>=now ? 0 : 1)
		}).thenBy((a: EventDate, b: EventDate)=>{
			return a.date>=now ? (a.date-b.date) : (b.date-a.date);
		}));
		
		var date_slice = sorted_dates.slice(page*PAGE_SIZE, (page*PAGE_SIZE)+PAGE_SIZE);
		var items : React.ReactElement[] = [];
		var is_past = false;
		for(var a of date_slice){
			if(a.date<=now && !is_past){
				is_past = true;
				items.push((
					<Table.Row header selectable={false} key={`PSTHDR`}>
						<Table.Cell empty colSpan={100} style={{ fontWeight: 'bold' }}>
							{t('select_date_modal.past_dates')}
						</Table.Cell>
					</Table.Row>
				))
			}
			var selected = selectedDates && selectedDates.indexOf(a.date_id)>-1;
			items.push((
				<Table.Row key={`DTSL-${a.date_id}`} selectable collapsingIndexes={[0, 2, 3]} onClick={onDateSelected(a.date_id)} data={[
					...(props.multiple!==false ? [
						<Icon name={!selected ? 'circle' : 'check-circle'} solid={!!selected} style={{ marginRight: 0, fontSize: 16, color: !!selected ? 'black' : '#CCC' }} />,
					] : []),
					a.date_id,
					fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
					a.city,
				]} />
			))
		}
		return items;
	}, [data, page, selectedDates, PAGE_SIZE, props.multiple]);

	var onSend = (setLoading: SetLoading)=>{
		if(!data || !props.onSelected) return;
		if(filterType===FilterType.ALL_EVENT) return props.onSelected(null, setLoading);
		if(!selectedDates) return props.onSelected([], setLoading)
		var dt = data.dates.filter(a=>selectedDates.indexOf(a.date_id)!==-1);
		return props.onSelected(dt, setLoading);
	}
	
	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>{props.title || t('select_date_modal.title')}</Modal.Header>
		<Modal.Content>
			{(!data || loadError) ? (
				<Header loading={!loadError} text={loadError ? t('common.error') : t('common.loading')} subtext={loadError} />
			) : <>
				{props.multiple!==false && (
					<SegmentControl 
						selected={filterType}
						onClick={setFilterType}
						style={{ marginBottom: 10 }}
						segments={[
							{ value: FilterType.DATES, text: t('common.event_date') },
							{ value: FilterType.RANGE, text: t('select_date_modal.date_range') },
							{ value: FilterType.ALL_EVENT, text: t('select_date_modal.all_event') },
						]}
					/>
				)}
				{(filterType===FilterType.DATES || props.multiple===false) ? (
					<Table striped fitted top={props.multiple===false} last>
						<Table.Head title={t('common.event_date')} />
						<Table.Body>
							{data.dates.length==0 ? (
								<Table.Cell empty row>{t('event_balance.no_dates')}</Table.Cell>
							) : DatesList}
						</Table.Body>
						{!!(data.dates && data.dates.length>PAGE_SIZE) && (
							<Table.Footer>
								<Table.Cell row style={{ textAlign: 'right' }} colSpan={100}>
									<Pagination page={page} onPageChange={setPage} pageCount={Math.ceil(data.dates.length/PAGE_SIZE)} />
								</Table.Cell>
							</Table.Footer>
						)}
					</Table>
				) : filterType===FilterType.RANGE ? <>
					<Input label={t('common.start_date')} readonly calendar={{
						initialDate: now.minus({ days: 7 }).toJSDate(),
						date: filterRange?.start?.toJSDate() || null,
						mode: 'date',
						onSelected: v=>setFilterRange(a=>({ ...a, start: DateTime.fromJSDate(v) })),
					}} />
					<Input label={t('common.end_date')} readonly calendar={{
						initialDate: now.toJSDate(),
						date: filterRange?.end?.toJSDate() || null,
						mode: 'date',
						onSelected: v=>setFilterRange(a=>({ ...a, end: DateTime.fromJSDate(v) })),
					}} />
					<Message type='info' centered>
						<Trans i18nKey={'select_date_modal.filtered_prediction'} count={filter_prediction} />
					</Message>
				</> : (
					<Message type='info' header={t('select_date_modal.all_event')} centered>
						<Trans i18nKey={'select_date_modal.filtered_prediction'} count={data.dates.length} />
					</Message>
				)}
			</>}
		</Modal.Content>
		<Modal.Actions>
			<Button text={t('common.close')} basic />
			{props.multiple!==false && (
				<Button text={t('common.continue')} color={'black'} disabled={!data} onClick={onSend} />
			)}
		</Modal.Actions>
	</Modal>
}

export default SelectDateModal;